import './Recruit.scss'
import './RecruitList.scss'
import { roleMarkerClass } from '../../../utils/roleMarker'
import './RecruitLanding.scss'
import './ChurchProfile.scss'
import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import axios from 'axios'
import MainURL from '../../../MainURL'

// 교회 프로필 (/church/:id)
// 서버가 같은 데이터로 크롤러용 HTML·메타를 먼저 응답하고, 이 화면은 사용자용으로 다시 그린다.
interface ProfileItem {
  kind: string
  id: number
  path: string
  title: string
  region: string
  position: string
  employment: string[]
  pay: string
  deadline: string
  postedAt: string
  state?: string
}

interface ProfileInfo {
  label: string
  value: string
  href?: string
}

interface ProfileData {
  name: string
  lead: string
  logo: string | null
  intro: string
  isVerified: boolean
  info: ProfileInfo[]
  stats: { total: number; active: number; since: string | null }
  active: ProfileItem[]
  past: ProfileItem[]
}

const PAST_STATE_LABEL: Record<string, string> = { closed: '마감', stale: '지난 공고' }

export default function ChurchProfile() {
  const { id } = useParams()
  const [data, setData] = useState<ProfileData | null>(null)
  const [state, setState] = useState<'loading' | 'ok' | 'notFound'>('loading')
  const [logoFailed, setLogoFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    setState('loading')
    setLogoFailed(false)
    axios
      .get(`${MainURL}/recruitseo/church/${id}`)
      .then((res) => {
        if (cancelled) return
        if (!res.data?.success) {
          setState('notFound')
          return
        }
        setData(res.data)
        setState('ok')
        document.title = `${res.data.name} 교회 정보·청빙 공고 | 사역자모아`
      })
      .catch(() => {
        if (!cancelled) setState('notFound')
      })
    return () => {
      cancelled = true
    }
  }, [id])

  return (
    <div className="recruit">
      <div className="inner">
        <div className="subpage__main recruit-landing church-profile">
          {state === 'loading' && <p className="recruit-landing__empty">불러오는 중...</p>}
          {state === 'notFound' && <p className="recruit-landing__empty">존재하지 않는 교회입니다.</p>}
          {state === 'ok' && data && (
            <>
              <div className="church-profile__head">
                <div className="church-profile__logo">
                  {data.logo && !logoFailed ? (
                    <img src={data.logo} alt={`${data.name} 로고`} onError={() => setLogoFailed(true)} />
                  ) : (
                    <span>{data.name.slice(0, 1)}</span>
                  )}
                </div>
                <div>
                  <h3 className="church-profile__name mm-serif">
                    {data.name}
                    {data.isVerified && <span className="church-profile__verified">인증 교회</span>}
                  </h3>
                  {data.lead && <p className="church-profile__lead">{data.lead}</p>}
                  <p className="church-profile__stats">
                    게시 중 {data.stats.active}건 ·
                    {data.stats.since ? ` ${data.stats.since}년부터` : ''} 등록된 청빙 {data.stats.total}건
                  </p>
                </div>
              </div>

              {data.intro && <p className="church-profile__intro">{data.intro}</p>}

              <div className="church-profile__cols">
                <div className="church-profile__col church-profile__col--main">
                  <section className="church-profile__section">
                    <h4>교회 정보</h4>
                    <dl className="church-profile__info">
                      {data.info.map((it) => (
                        <div key={it.label}>
                          <dt>{it.label}</dt>
                          <dd>
                            {it.href ? (
                              <a href={it.href} target="_blank" rel="noopener noreferrer nofollow">{it.value}</a>
                            ) : (
                              it.value
                            )}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </section>

                  {data.past.length > 0 && (
                    <section className="church-profile__section">
                      <h4>지난 청빙 이력</h4>
                      <ul className="church-profile__history">
                        {data.past.map((item) => (
                          <li key={`${item.kind}-${item.id}`}>
                            <span className="church-profile__date">{item.postedAt}</span>
                            <Link to={item.path}>{item.title}</Link>
                            <span className="church-profile__state">
                              {[item.position, PAST_STATE_LABEL[item.state || ''] || '종료'].filter(Boolean).join(' · ')}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                </div>
                <div className="church-profile__col church-profile__col--side">
                  <section className="church-profile__section">
                    <h4>게시 중인 청빙 공고 {data.active.length}건</h4>
                    {data.active.length === 0 ? (
                      <p className="church-profile__none">현재 게시 중인 공고가 없습니다.</p>
                    ) : (
                      <ul className="recruit-landing__list">
                        {data.active.map((item) => (
                          <li key={`${item.kind}-${item.id}`}>
                            <Link to={item.path} className="recruit-landing__card">
                              {item.position && <span className={roleMarkerClass(item.position)}>{item.position}</span>}
                              <div className="recruit-landing__title">{item.title}</div>
                              <div className="recruit-landing__meta">
                                {[item.region, item.employment.join('·'), item.pay].filter(Boolean).join(' · ')}
                              </div>
                              <div className="recruit-landing__deadline">{item.deadline}</div>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>

                  <Link to="/recruit" className="list-btn recruit-landing__all">
                    <span>전체 공고 보기 →</span>
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
